import * as Sentry from '@sentry/browser';
import { createStorefrontApiClient } from '@shopify/storefront-api-client';
import isEqual from 'lodash/isEqual';
import React, { useEffect, useState } from 'react';

import { useCustomer } from '@/hooks/useCustomer';
import { useIPLocation } from '@/hooks/useIPLocation';
import { getAudience } from '@/utils/getAudience';

import {
  HEM_COMMERCE_BASE_PATH,
  IS_DEVELOPMENT,
  IS_PRO,
  REGION,
  RegionEnum,
  SHOPIFY_DOMAIN,
} from '../constants';
import {
  AddLineItemsInput,
  RemoveLineItemsInput,
  StoreStatus,
  UpdateLineItemsInput,
  ValidateResponse,
  ValidateResponseErrorMessage,
} from './cart-context-types';
import { getDefaultCountry } from './getDefaultCountry';
import { cartBuyerIdentityUpdate } from './graphql/cart-buyer-identity-update-mutation';
import { cartAttributesUpdate } from './graphql/cart-cart-attributes-update-mutation';
import { cartCreate } from './graphql/cart-create-mutation';
import { cartDiscountCodesUpdate } from './graphql/cart-discount-codes-update-mutation';
import { cartFetch } from './graphql/cart-fetch-query';
import { cartLinesAdd } from './graphql/cart-lines-add-mutation';
import { cartLinesRemove } from './graphql/cart-lines-remove-mutation';
import { cartLinesUpdate } from './graphql/cart-lines-update-mutation';
import type { Cart } from './graphql/fragments/cart-fragment';

const localStorageKey = `hem-${
  process.env.GATSBY_REGION_REGION_DIFFERENTIATOR
}${IS_DEVELOPMENT ? '-dev' : ''}-cart-id`
  .replace(' ', '-')
  .toLowerCase();

const localStorageErrorKey = `hem-${
  process.env.GATSBY_REGION_REGION_DIFFERENTIATOR
}${IS_DEVELOPMENT ? '-dev' : ''}-cart-validation-errors`
  .replace(' ', '-')
  .toLowerCase();

const client = createStorefrontApiClient({
  storeDomain: SHOPIFY_DOMAIN,
  apiVersion: '2024-07',
  publicAccessToken: process.env.GATSBY_SHOPIFY_STOREFRONT_TOKEN,
  retries: 3,
  customFetchApi: fetch,
});

export const CartContext = React.createContext<{
  cart: Cart;
  addLineItems: ({ lines }: AddLineItemsInput) => Promise<Cart>;
  removeLineItems: ({ lineIds }: RemoveLineItemsInput) => Promise<Cart>;
  updateLineItems: ({ lines }: UpdateLineItemsInput) => Promise<Cart>;
  updateAttributes: (cart: Cart) => Promise<Cart>;
  addDiscountCode: ({
    discountCode,
  }: {
    discountCode: string;
  }) => Promise<Cart>;
  removeDiscountCode: ({
    discountCode,
  }: {
    discountCode: string;
  }) => Promise<Cart>;
  // addCheckoutNote: () => Promise<void>;
  status: StoreStatus;
  errors: ValidateResponseErrorMessage[];
}>({
  cart: null,
  addLineItems: null,
  removeLineItems: null,
  updateLineItems: null,
  updateAttributes: null,
  addDiscountCode: null,
  removeDiscountCode: null,
  // addCheckoutNote: null,
  status: StoreStatus.IDLE,
  errors: [],
});

export const StoreProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const store = getAudience();
  const { customer } = useCustomer();
  const { data: ipData } = useIPLocation();

  let country: string;
  try {
    country =
      REGION === RegionEnum.Eu
        ? ipData?.country || getDefaultCountry()
        : getDefaultCountry();
  } catch (error) {
    console.log('[CartContext]', error);
    Sentry.captureException(error);
  }

  const [cart, setCart] = useState<Cart>(null);
  const [status, setStatus] = useState<StoreStatus>(StoreStatus.IDLE);
  const [errors, setErrors] = useState<ValidateResponseErrorMessage[]>([]);

  const createCart = async ({
    country,
    customer,
  }: {
    country: string;
    customer?: { email: string };
  }): Promise<Cart> => {
    try {
      const {
        data,
        // errors,
        // extensions
      } = await client.request<{
        cartCreate: { cart: Cart };
      }>(cartCreate, {
        variables: {
          country,
          email: customer?.email,
        },
      });

      if (data?.cartCreate?.cart) {
        return data.cartCreate.cart;
      }
    } catch (error) {
      console.log('[CartContext] createCart', error);
      Sentry.captureException(error);
    }
  };

  const fetchCart = async ({
    cartId,
    country,
    customer,
  }: {
    cartId: string;
    country: string;
    customer?: { email: string };
  }): Promise<Cart> => {
    try {
      const {
        data,
        errors,
        // extensions
      } = await client.request<{
        cart: Cart;
      }>(cartFetch, {
        variables: {
          country,
          email: customer?.email,
          cartId,
        },
      });

      if (errors) {
        throw new Error(JSON.stringify(errors));
      }

      if (data?.cart) {
        const {
          data: cartBuyerIdentityUpdateData,
          errors,
          // extensions
        } = await client.request<{
          cartBuyerIdentityUpdate: { cart: Cart };
        }>(cartBuyerIdentityUpdate, {
          variables: {
            country,
            cartId: data.cart.id,
            buyerIdentityInput: {
              countryCode: country,
            },
          },
        });

        if (errors) {
          throw new Error(JSON.stringify(errors));
        }

        if (cartBuyerIdentityUpdateData?.cartBuyerIdentityUpdate?.cart) {
          return cartBuyerIdentityUpdateData.cartBuyerIdentityUpdate.cart;
        }

        return data.cart;
      }
    } catch (error) {
      console.log('[CartContext] fetchCart', error);
      Sentry.captureException(error);
    }
  };

  const initCart = async () => {
    try {
      const cartId = localStorage.getItem(localStorageKey);
      let cart;
      if (cartId) {
        cart = await fetchCart({ cartId, country });
      } else {
        cart = await createCart({ country });
      }
      if (cart) {
        setCart(cart);
        return cart;
      }
    } catch (error) {
      console.log('[CartContext] initCart', error);
      Sentry.captureException(error);
    }
  };

  const addLineItems = async ({ lines }: AddLineItemsInput) => {
    try {
      setStatus(StoreStatus.ADDING);

      let cartId = cart?.id;
      if (!cartId) {
        const newCart = await createCart({ country });
        cartId = newCart.id;
        setCart(newCart);
      }

      const {
        data,
        errors,
        // extensions
      } = await client.request<{
        cartLinesAdd: { cart: Cart };
      }>(cartLinesAdd, {
        variables: {
          country,
          email: customer?.email,
          cartId,
          lines,
        },
      });

      if (errors) {
        throw new Error(JSON.stringify(errors));
      }

      if (data?.cartLinesAdd?.cart) {
        setCart(data.cartLinesAdd.cart);
        setStatus(StoreStatus.IDLE);
        const cart = await updateAttributes(data.cartLinesAdd.cart);
        return cart;
      }

      setStatus(StoreStatus.IDLE);
    } catch (error) {
      console.log('[CartContext] addLineItems', error);
      Sentry.captureException(error);
      setStatus(StoreStatus.IDLE);
    }
  };

  const removeLineItems = async ({ lineIds }: RemoveLineItemsInput) => {
    try {
      setStatus(StoreStatus.REMOVING);

      let cartId = cart?.id;
      if (!cartId) {
        const newCart = await createCart({ country });
        cartId = newCart.id;
        setCart(newCart);
      }

      const {
        data,
        errors,
        // extensions
      } = await client.request<{
        cartLinesRemove: { cart: Cart };
      }>(cartLinesRemove, {
        variables: {
          country,
          email: customer?.email,
          cartId,
          lineIds,
        },
      });

      if (errors) {
        throw new Error(JSON.stringify(errors));
      }

      if (data?.cartLinesRemove?.cart) {
        setCart(data.cartLinesRemove.cart);
        setStatus(StoreStatus.IDLE);
        const cart = await updateAttributes(data.cartLinesRemove.cart);
        return cart;
      }

      setStatus(StoreStatus.IDLE);
    } catch (error) {
      console.log('[CartContext] removeLineItems', error);
      Sentry.captureException(error);
      setStatus(StoreStatus.IDLE);
    }
  };

  const updateLineItems = async ({ lines }: UpdateLineItemsInput) => {
    try {
      setStatus(StoreStatus.UPDATING);

      let cartId = cart?.id;
      if (!cartId) {
        const newCart = await createCart({ country });
        cartId = newCart.id;
        setCart(newCart);
      }

      const {
        data,
        errors,
        // extensions
      } = await client.request<{
        cartLinesUpdate: { cart: Cart };
      }>(cartLinesUpdate, {
        variables: {
          country,
          email: customer?.email,
          cartId,
          lines,
        },
      });

      if (errors) {
        throw new Error(JSON.stringify(errors));
      }

      if (data?.cartLinesUpdate?.cart) {
        setCart(data.cartLinesUpdate.cart);
        setStatus(StoreStatus.IDLE);
        const cart = await updateAttributes(data.cartLinesUpdate.cart);
        return cart;
      }

      setStatus(StoreStatus.IDLE);
    } catch (error) {
      console.log('[CartContext] updateLineItems', error);
      Sentry.captureException(error);
      setStatus(StoreStatus.IDLE);
    }
  };

  const updateErrorStorageAndState = (
    errors: ValidateResponseErrorMessage[],
  ) => {
    try {
      if (localStorage) {
        localStorage.setItem(localStorageErrorKey, JSON.stringify(errors));
      }
      setErrors(errors);
    } catch (error) {
      console.log('[CartContext] updateErrorStorageAndState', error);
      Sentry.captureException(error);
    }
  };

  const updateAttributes = async (localCart: Cart) => {
    Sentry.addBreadcrumb({
      category: 'shopify',
      message: 'updateAttributes',
      level: 'info',
      data: { cart: localCart },
    });

    try {
      const items = localCart.lines.nodes.map((item) => ({
        sku: item.merchandise.sku,
        quantity: item.quantity,
      }));

      const attributes: {
        key: string;
        value: string;
      }[] = [];

      if (items.length > 0) {
        const url = `${HEM_COMMERCE_BASE_PATH}/inventories/validate/${store}/`;
        const resp = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items,
            checkout_id: localCart.id,
            email: customer?.email,
            options: {
              check_assemblies: IS_PRO,
            },
          }),
        });

        if (!resp.ok) {
          const text = await resp.text();
          throw Error(text);
        }

        const json: ValidateResponse = await resp.json();

        if (json.messages?.length > 0) {
          updateErrorStorageAndState(json.messages);
        } else {
          updateErrorStorageAndState([]);
        }

        const dateAttributes = [
          'promised_delivery_date',
          'shipment_date',
          'earliest_delivery_date',
          'latest_delivery_date',
        ];

        dateAttributes.forEach((attr) => {
          const value = json[attr as keyof ValidateResponse];
          if (typeof value === 'string' && value.trim() !== '') {
            attributes.push({
              key: attr,
              value: value,
            });
          }
        });

        attributes.push({
          key: 'validated_at',
          value: new Date().toISOString(),
        });
      }

      const oldAttrs = localCart.attributes.map((attr) => ({
        key: attr.key,
        value: attr.value,
      }));

      const newAttrs = attributes.map((attr) => ({
        key: attr.key,
        value: attr.value,
      }));

      if (!isEqual(oldAttrs, newAttrs)) {
        Sentry.addBreadcrumb({
          category: 'shopify',
          message: 'Old checkout attributes ' + JSON.stringify(oldAttrs),
          level: 'info',
        });

        Sentry.addBreadcrumb({
          category: 'shopify',
          message: 'New checkout attributes ' + JSON.stringify(newAttrs),
          level: 'info',
        });

        const {
          data,
          errors,
          //  extensions
        } = await client.request<{
          cartAttributesUpdate: { cart: Cart };
        }>(cartAttributesUpdate, {
          variables: {
            country,
            cartId: localCart.id,
            attributes,
          },
        });

        if (errors) {
          throw new Error(JSON.stringify(errors));
        }

        if (data?.cartAttributesUpdate?.cart) {
          Sentry.addBreadcrumb({
            category: 'shopify',
            message:
              'Updated checkout attributes ' +
              JSON.stringify(data?.cartAttributesUpdate?.cart?.attributes),
            level: 'info',
          });

          return data.cartAttributesUpdate.cart;
        }

        return localCart;
      }

      return localCart;
    } catch (error) {
      console.log('[CartContext] updateAttributes', error);
      Sentry.captureException(error);
      return localCart;
    }
  };

  const addDiscountCode = async ({
    discountCode,
  }: {
    discountCode: string;
  }) => {
    try {
      setStatus(StoreStatus.UPDATING);

      let cartId = cart?.id;
      if (!cartId) {
        const newCart = await createCart({ country });
        cartId = newCart.id;
        setCart(newCart);
      }

      const {
        data,
        errors,
        // extensions
      } = await client.request<{
        cartDiscountCodesUpdate: { cart: Cart };
      }>(cartDiscountCodesUpdate, {
        variables: {
          country,
          cartId,
          discountCodes: [
            discountCode,
            ...(cart.discountCodes || []).map((code) => code.code),
          ],
        },
      });

      if (errors) {
        throw new Error(JSON.stringify(errors));
      }

      if (data?.cartDiscountCodesUpdate?.cart) {
        setCart(data.cartDiscountCodesUpdate.cart);
        setStatus(StoreStatus.IDLE);
        const cart = await updateAttributes(data.cartDiscountCodesUpdate.cart);
        return cart;
      }

      setStatus(StoreStatus.IDLE);
    } catch (error) {
      console.log('[CartContext] addDiscountCode', error);
      Sentry.captureException(error);
      setStatus(StoreStatus.IDLE);
    }
  };

  const removeDiscountCode = async ({
    discountCode,
  }: {
    discountCode: string;
  }) => {
    try {
      setStatus(StoreStatus.UPDATING);

      let cartId = cart?.id;
      if (!cartId) {
        const newCart = await createCart({ country });
        cartId = newCart.id;
        setCart(newCart);
      }

      const {
        data,
        errors,
        // extensions
      } = await client.request<{
        cartDiscountCodesUpdate: { cart: Cart };
      }>(cartDiscountCodesUpdate, {
        variables: {
          country,
          cartId,
          discountCodes: (cart.discountCodes || [])
            .map((code) => code.code)
            .filter((code) => code !== discountCode),
        },
      });

      if (errors) {
        throw new Error(JSON.stringify(errors));
      }

      if (data?.cartDiscountCodesUpdate?.cart) {
        setCart(data.cartDiscountCodesUpdate.cart);
        setStatus(StoreStatus.IDLE);
        const cart = await updateAttributes(data.cartDiscountCodesUpdate.cart);
        return cart;
      }

      setStatus(StoreStatus.IDLE);
    } catch (error) {
      console.log('[CartContext] removeDiscountCode', error);
      Sentry.captureException(error);
      setStatus(StoreStatus.IDLE);
    }
  };

  // const addCheckoutNote = () => Promise.resolve();

  // Fetch or create a cart on mount
  useEffect(() => {
    try {
      initCart();
    } catch (error) {
      console.log('[CartContext]', error);
      Sentry.captureException(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Store cart ID in local storage
  useEffect(() => {
    try {
      if (cart?.id && localStorage) {
        localStorage.setItem(localStorageKey, cart.id);
      }
    } catch (error) {
      console.log('[CartContext]', error);
      Sentry.captureException(error);
    }
  }, [cart?.id]);

  // Set buyerIdentityInput on cart
  useEffect(() => {
    if (customer?.email && cart?.id) {
      const updateBuyerIdentity = async () => {
        try {
          const {
            data,
            errors,
            // extensions
          } = await client.request<{
            cartBuyerIdentityUpdate: { cart: Cart };
          }>(cartBuyerIdentityUpdate, {
            variables: {
              country,
              cartId: cart.id,
              buyerIdentityInput: {
                countryCode: country,
                email: customer.email,
              },
            },
          });

          if (errors) {
            throw new Error(JSON.stringify(errors));
          }

          if (data?.cartBuyerIdentityUpdate?.cart) {
            setCart(data.cartBuyerIdentityUpdate.cart);
          }
        } catch (error) {
          console.log('[CartContext] cartBuyerIdentityUpdate', error);
          Sentry.captureException(error);
        }
      };

      updateBuyerIdentity();
    }
  }, [cart?.id, country, customer?.email]);

  return (
    <CartContext.Provider
      value={{
        addLineItems,
        removeLineItems,
        updateLineItems,
        updateAttributes,
        addDiscountCode,
        removeDiscountCode,
        // addCheckoutNote,
        cart,
        status,
        errors,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
